import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from '../app/store';
import { replaceWithComboItem } from '../reducers/menuSlice';
import { analyzeItemConversion } from '../utils/itemConversion';
import { analyzeOptionReplacement } from '../utils/optionReplacement';
import { ParsedModifierGroup } from '../types/menu';
import { CartItem } from '../utils/cart';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { selectFeatureFlagItemConversion } from '../redux/features/config/config.selector';
import {
  selectItemConversionBusinessRules,
  selectUnavailableItemsByName,
} from '../selectors/menu';
import { IBusinessRule } from '../types/brandMenu';
import {
  CartScope,
  MAIN_CART_SCOPE,
} from '../redux/features/draftCart/cartScope';
import {
  cartActionsForScope,
  cartItemsForScope,
} from '../redux/features/draftCart/cartScope.routing';

/**
 * Hook for handling both item conversion and option replacement.
 *
 * HITL-1617 — `cartScope` says which cart the caller is editing, and it is the
 * caller's to declare rather than something this hook reads off `dialogSlice`.
 * Both of the things below REWRITE a cart item the caller named: unscoped, a
 * combo-size pick on a My Cart line would delete and rebuild the AI's item of
 * the same id — HITL-1606's invariant broken by a write the agent never asked
 * for. Defaults to `main`, so `AutocompleteComp` — still order-only until
 * HITL-1618 — is unchanged.
 */
const useItemConversion = (cartScope: CartScope = MAIN_CART_SCOPE) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const isItemConversionEnabled = useSelector(selectFeatureFlagItemConversion);
  const {
    comboConversion,
    aLaCarteConversion,
    defaultSizeSelection,
    comboSizeTransformation,
  } = useSelector(selectItemConversionBusinessRules);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);

  // Extract combo conversion business rules from brand menu
  const itemConversionRules: IBusinessRule[] = useMemo(
    () => [...(comboConversion || []), ...(aLaCarteConversion || [])],
    [comboConversion, aLaCarteConversion]
  );

  const handleItemConversion = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      const conversionInfo = analyzeItemConversion(
        modGroup.name || '',
        selectedMenuItemName,
        currentCartItem,
        itemConversionRules
      );

      if (conversionInfo.shouldConvert) {
        dispatch(
          replaceWithComboItem({
            originalCartItem: currentCartItem,
            targetItemName: conversionInfo.targetItemId!,
            comboSize: conversionInfo.conversionSize,
            optionsToPreSelect: conversionInfo.optionsToPreSelect,
            cartScope,
          })
        );
      }
    },
    [cartScope, dispatch, itemConversionRules]
  );

  // Extract option replacement business rules (default size selection + combo size transformation)
  const optionReplacementRules: IBusinessRule[] = useMemo(
    () => [...(defaultSizeSelection || []), ...(comboSizeTransformation || [])],
    [defaultSizeSelection, comboSizeTransformation]
  );

  const handleOptionReplacement = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      // The caller dispatches the triggering modifier selection (e.g. the new combo
      // size) synchronously before invoking this, but `currentCartItem` is the
      // pre-selection snapshot captured during the component's render. Re-read the
      // freshly-updated cart item from the store so availability checks evaluate
      // against the new selection instead of the previous one — otherwise a combo
      // size change is checked against the prior size and the dependent side/drink
      // sizes are wrongly judged unavailable.
      //
      // HITL-1617: read back from the cart the caller is editing. Both slices
      // number `cartItemId` from 1, so `state.cart.cartItems[id]` resolves to a
      // DIFFERENT item when the scope is `draft` — availability would then be
      // judged against whatever the AI happens to have at that id.
      const latestCartItem =
        (currentCartItem &&
          cartItemsForScope(store.getState(), cartScope)?.[
            currentCartItem.cartItemId
          ]) ??
        currentCartItem;

      const replacementInfo = analyzeOptionReplacement(
        modGroup.name || '',
        selectedMenuItemName,
        latestCartItem,
        optionReplacementRules,
        unavailableItemsByName
      );

      if (replacementInfo.shouldApplyReplacement) {
        // Apply option replacement by dispatching the new action
        dispatch(
          cartActionsForScope(cartScope).applyOptionReplacement({
            cartItemId: latestCartItem.cartItemId,
            targetModifierGroups: replacementInfo.targetModifierGroups,
            unavailableItemsByName,
          })
        );
      }
    },
    [cartScope, dispatch, store, optionReplacementRules, unavailableItemsByName]
  );

  const handleModifierSelection = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      // Check feature flags before running item conversion or option replacement
      // Both unified menu and item conversion feature flags must be enabled
      const isUnifiedMenu = getMenuUnificationFeatureFlag();
      if (!isUnifiedMenu || !isItemConversionEnabled) {
        return;
      }

      // First check for item conversion
      const conversionInfo = analyzeItemConversion(
        modGroup.name || '',
        selectedMenuItemName,
        currentCartItem,
        itemConversionRules
      );

      if (conversionInfo.shouldConvert) {
        // If conversion happens, let the thunk handle option replacement
        dispatch(
          replaceWithComboItem({
            originalCartItem: currentCartItem,
            targetItemName: conversionInfo.targetItemId!,
            comboSize: conversionInfo.conversionSize,
            optionsToPreSelect: conversionInfo.optionsToPreSelect,
          })
        );
      } else {
        // Only check for option replacement if no conversion happens
        handleOptionReplacement(
          modGroup,
          selectedMenuItemName,
          currentCartItem
        );
      }
    },
    [
      dispatch,
      handleOptionReplacement,
      isItemConversionEnabled,
      itemConversionRules,
    ]
  );

  return {
    handleItemConversion,
    handleOptionReplacement,
    handleModifierSelection,
  };
};

export default useItemConversion;
